/*
 * Copyright (c) 2019 Pegasystems Inc.
 * All rights reserved.
 */
import Utils from '../utils/utils';
import * as StateManager from '../store/state/state-manager';
import * as RulesStore from '../rules/rules-store';
import AnnotationProcessor from '../annotation/processor';
import Constants from '../constants';
import { mergeData } from '../store/state/actions/action-creators';
import { getValue } from '../store/state/state-manager';
import { C11nEnv } from '../interpreter/c11n-env';
import type { ComponentMetadataConfig, Meta } from '../interpreter/types';
import type { NodeProps } from '../contextree/types';

const PAGELISTS = Constants.SUMMARY_OF_LISTS;
const WHENS = Constants.SUMMARY_OF_WHEN_CONDITIONS;

/**
 * This handles all rule resolution or fetching the right rule
 * for rendering.
 * It also contains the logic to maintain the context (stepPage)
 * so that components can render without much effort.
 */
class ContextProcessor {
  /**
   * Merges the data with existing data and also
   * updates the redux store.
   * @param data target data object
   */
  static updateData(data: any) {
    Object.keys(data).forEach((context) => {
      StateManager.dispatch(
        mergeData({
          context,
          data: data[context]
        })
      );
    });
  }

  /**
   * Updates the context data with incoming contextdata
   * @param ctxData contextdata
   */
  static updateContextData(ctxData: any) {
    Object.keys(ctxData).forEach((context) => {
      StateManager.dispatch(
        mergeData({
          context,
          data: ctxData[context]
        })
      );
    });
  }

  /**
   * Resolves the when from the contextdata and
   * returns the when result- true or false.
   * @param whenName name of the when-rule
   * @param c11nEnv constellation env
   */
  static resolveWhen(whenName: string, c11nEnv: C11nEnv) {
    return !!c11nEnv.getContextDataValue(`.${WHENS}.${whenName}`);
  }

  /**
   * Returns the localized value for the given field value.
   * @param fieldValue field value
   * @param c11nEnv constellation env
   */
  static resolveFieldValue(fieldValue: string, c11nEnv: C11nEnv) {
    return c11nEnv.getLocalizedValue(fieldValue);
  }

  /**
   * Resolves the navigation from contextdata and returns the
   * UIMetadata of the navigation.
   * @param ref navigation name or property reference.
   * @param c11nEnv constellation env
   */
  static resolveNavigation(ref: string | undefined, c11nEnv: C11nEnv) {
    const resolvedName = this.resolveName(ref, c11nEnv);
    return RulesStore.resolveNavigation(typeof resolvedName === 'string' ? resolvedName : '');
  }

  /**
   * Resolves the navigation from contextdata and returns the
   * UIMetadata of the navigation.
   * @param ref navigation name or property reference.
   * @param c11nEnv constellation env
   * @param classID case type class name
   */
  static resolveView(ref: string | undefined, c11nEnv: C11nEnv, classID: string = '') {
    const views: { [key: string]: any } = RulesStore.resolveView(this.resolveName(ref, c11nEnv));
    let view: any[] = [];
    // If there are multiple views with the same name, resolve them with the classID
    if (Array.isArray(views) && views.length > 0) {
      if (c11nEnv && views.length > 1) {
        if (!classID) classID = c11nEnv.getValue(Constants.CLASS_ID) || '';
        if (!classID) return;
        view = views.filter((viewObj) => {
          return viewObj[Constants.CLASSID] === classID;
        });
      }
      // If no view with the classID as context class exists, return the first view.(Safe check)
      if (view.length === 0) {
        return views[0];
      }
      return view[0];
    }
    return views;
  }

  /**
   * Special handling to support parameter holding the
   * rule name.
   * @param name name of the param
   * @param c11nEnv constellation env
   */
  static resolveName(name: unknown, c11nEnv: C11nEnv) {
    if (name && typeof name === 'string' && name.startsWith('param.')) {
      name = name.substring('param.'.length);
      return c11nEnv.getContextDataValue(`.$parameters.${name}`);
    }
    if (name && typeof name === 'string' && name.startsWith('@')) {
      name = AnnotationProcessor.handleAnnotation(name, c11nEnv);
    }
    return name;
  }

  /**
   * Prepares full reference for current context.
   * If currentReference is "pyWorkpage" & newReference is ".Page1", output => pyWorkpage.Page1
   * If currentReference is "pyWorkpage" & newReference is "Page1", output => Page1
   * @param currentReference base reference
   * @param newReference new reference to build
   */
  static getContextPath(currentReference: string, newReference?: string | number) {
    if (typeof newReference !== 'number' && !newReference) {
      return currentReference;
    }
    newReference = AnnotationProcessor.trimAnnotation(newReference);

    if (typeof newReference === 'number' || typeof newReference === 'string') {
      if (typeof newReference === 'number' && Number.isInteger(newReference)) {
        return `${currentReference}[${newReference}]`;
      }
      if (typeof newReference === 'string' && newReference.startsWith('.')) {
        return currentReference + newReference;
      }
      return newReference as string;
    }
    return currentReference;
  }

  /**
   * resolves and returns ruleClass if present in the config
   * @param c11nEnv c11nEnv object
   * @param context context name
   * @param config config object
   * @returns returns the ruleClass string
   * @private
   */
  static getRuleClass(c11nEnv: C11nEnv, context?: string, config?: ComponentMetadataConfig): string {
    let ruleClass;
    if (context) {
      ruleClass = context.startsWith(Constants.CLASS_ANNOTATION)
        ? context.split(Constants.CLASS_ANNOTATION)[1].trim()
        : this.resolveClassID(
            context,
            c11nEnv.getPageReference(),
            c11nEnv.getContextName(),
            c11nEnv.getValue(Constants.CASE_INFO.CASE_INFO_CLASSID)
          );
    } else if (config?.authorContext) {
      const fieldMetadata = c11nEnv.getFieldMetadata(config.authorContext);
      // Views for multi-record reference live in the target class of the field, otherwise in the class of the field
      if (fieldMetadata) {
        const isReference = ['refer', 'copy'].includes(fieldMetadata.dataRetrievalType);
        const isMulti = fieldMetadata.type === 'Page List';
        ruleClass = isReference && isMulti ? fieldMetadata.pageClass : fieldMetadata.classID;
      }
      if (!ruleClass) {
        ruleClass = config.ruleClass;
      }
    }
    return ruleClass;
  }

  /**
   * If component's config has reference (like Section, Harness, flowAction),
   * it would be resolved from that particulat store and returns metadata of that entry.
   * @param component - metadata of component
   * Sample component config {"type":"reference","config":{"type":"section","ref":"MainSec"}}
   * @param c11nEnv Constellation env
   */
  static resolveReference(component: Meta, c11nEnv: C11nEnv) {
    const { type } = component;
    let referenceRuleMeta;
    if (type === 'reference') {
      const { config } = component;
      const refType = config?.type;
      const { ref, name, context } = config || {};
      const ruleClass = this.getRuleClass(c11nEnv, context, config);
      if (c11nEnv) {
        c11nEnv._ruleClass = ruleClass;
      }
      // if (refType === "section") {
      //   referenceRuleMeta = this.resolveSection(ref, c11nEnv);
      // } else if (refType === "harness") {
      //   referenceRuleMeta = this.resolveHarness(ref, c11nEnv);
      // } else
      // if (refType === "flowaction") {
      //   referenceRuleMeta = this.resolveFlowAction(ref, c11nEnv);
      // } else
      if (refType === 'navigation') {
        referenceRuleMeta = this.resolveNavigation(ref || name, c11nEnv);
      } else if (refType === 'view') {
        referenceRuleMeta = this.resolveView(ref || name, c11nEnv, ruleClass || '');
      } else if (refType === 'widget') {
        // Will be updated further for future release, temporary handling for Widgets
        referenceRuleMeta = { type: 'Widget', config: { name, ruleClass } };
      }
      return referenceRuleMeta;
    }
    return component;
  }

  /**
   * Tokenizes and resolves property.
   * @param pageReference context path
   * @param propReference property reference eq., .FirstName or pyWorkPage.FirstName
   * @param context context name
   */
  static resolvePropertyReference(
    pageReference: string | null | undefined,
    propReference?: string,
    context?: string | null
  ) {
    return StateManager.getValue(propReference, pageReference, context);
  }

  /**
   * Returns the property object or string depends on the
   * property.
   * example:
   * PageList: D_Genders.pxResults, will return list object
   * Page: pyWorkPage, will return workpage object.
   * Property: pyWorkPage.FirstName. will return string value.
   * @param propName property reference.
   * @param pageReference pageReference path
   * @param context context name
   */
  static getProperty(propName: string, pageReference?: string | null, context?: string | null) {
    let propertyRef = this.resolvePropertyReference(pageReference, propName, context);
    // It might miss in case of parameterized datapages, where
    // actual datapage name is different from downloaded one.
    if (propertyRef === null || propertyRef === undefined || propertyRef.length === 0) {
      const pageListContextData = StateManager.lookupContextValue(`${pageReference}.${PAGELISTS}`, propName, context);
      if (pageListContextData) {
        // Resolve again
        propertyRef = this.resolvePropertyReference(pageReference, pageListContextData, context);
      }
    }

    return propertyRef;
  }

  /**
   * Returns the string value of the property.
   * @param prop property reference
   * @param pageReference pageReference
   * @param context context name
   */
  static getPropertyValue(prop: string, pageReference?: string | null, context?: string | null) {
    prop = AnnotationProcessor.trimAnnotation(prop);
    return this.getProperty(prop, pageReference, context);
  }

  static getReferencedUser(userId: string, contextTreeNodeProps: NodeProps) {
    const refUsers = ContextProcessor.getLitePropertyValue(contextTreeNodeProps, 'referencedUsers');

    if (Array.isArray(refUsers)) {
      const userObj = refUsers.find((user) => user.UserID === userId);

      if (userObj) {
        return {
          userId: userObj.UserID,
          UserName: userObj.UserName
        };
      }
    }

    return refUsers?.[userId];
  }

  static getLitePropertyValue(contextTreeNodeProps?: NodeProps, propName?: string) {
    if (!contextTreeNodeProps) {
      return undefined;
    }
    const {
      useCustomContext,
      propertyName: contextTreeNodeProperty,
      hasSuggestions,
      pageReference,
      context
    } = contextTreeNodeProps;
    let prop = propName || contextTreeNodeProperty || '';

    if (useCustomContext) {
      prop = prop.startsWith('.') ? prop.substring(1) : prop;
      return prop
        .split('.')
        .reduce((customContext: string, split: any) => customContext?.[split] ?? undefined, useCustomContext as any);
    }
    if (hasSuggestions && prop === contextTreeNodeProperty) {
      return ContextProcessor.getPropertyValue(
        prop,
        pageReference,
        context ? `${context}/${Constants.SUGGESTION}` : undefined
      );
    }

    return ContextProcessor.getPropertyValue(prop, pageReference, context || undefined);
  }

  static _handlePropString(
    config: { [key: string]: any },
    key: string,
    value: string,
    c11nEnv: C11nEnv,
    contextTreeNodeProps?: NodeProps
  ) {
    if (value.startsWith('@')) {
      config[key] = AnnotationProcessor.handleAnnotation(value, c11nEnv, contextTreeNodeProps);
    } else {
      config[key] = value;
    }
  }

  static _resolveProps(config: object, c11nEnv?: C11nEnv, contextTreeNodeProps?: NodeProps) {
    if (!config) {
      return;
    }

    Object.keys(config).forEach((key) => {
      const value = config[key as keyof typeof config];
      if (typeof value === 'string') {
        this._handlePropString(config, key, value, c11nEnv as C11nEnv, contextTreeNodeProps);
      } else if (Array.isArray(value)) {
        (value as any).forEach((p: string) => {
          this._resolveProps(
            typeof p === 'object' ? p : config[p as keyof typeof config],
            c11nEnv,
            contextTreeNodeProps
          );
        });
      } else if (typeof value === 'object') {
        this._resolveProps(config[key as keyof typeof config], c11nEnv, contextTreeNodeProps);
      }
    });
  }

  /**
   * resolveProps to resolve all properties in action config
   * @param config config object to resolve properties
   * @param newConfig target object
   * @param c11nEnv constellation env
   * @returns returns the newConfig
   */
  static resolveProps(config: any, newConfig: any, c11nEnv: C11nEnv) {
    if (!config || !newConfig) {
      return newConfig;
    }
    Utils.mergeDeep(config, newConfig);
    this._resolveProps(newConfig, c11nEnv, undefined);
    return newConfig;
  }

  static resolveContextTreeNodeProps(contextTreeNodeProps: NodeProps) {
    const newConfig = {};
    if (!contextTreeNodeProps) {
      return newConfig;
    }
    Utils.mergeDeep(contextTreeNodeProps, newConfig);

    this._resolveProps(newConfig, undefined, contextTreeNodeProps);
    return Utils.resolveStringBooleans(newConfig);
  }

  /**
   * @private
   */
  static resolvePropertyPageClass(fullRef: string, propName: string, baseClassID: string): string | undefined {
    const parentPageClass = Utils.resolvePageClass(fullRef, baseClassID);
    const prop = propName.startsWith('.') ? propName.slice(1) : propName;
    const fieldMetadata = RulesStore.getPropertyMetadata(prop, parentPageClass);
    let classID;
    if (!fieldMetadata) return;
    if (Array.isArray(fieldMetadata)) {
      classID = fieldMetadata[0]?.pageClass;
    } else {
      classID = fieldMetadata.pageClass;
    }
    return classID;
  }

  /**
   *
   * Resolve classID for provided property
   * @param prop - property name like .FirstName or .Student.FirstName
   * @param pageReference - property reference like caseInfo.Content
   * @param context - context info like app/primary/workarea_2
   * @example caseInfo: {
   *  content: {
   *    P1: {
   *        classID: 'Data-P1',
   *        P2: {
   *          classID: 'Data-P2',
   *          t1: 'constellation'
   *        }
   *     }
   *  }
   * }
   * tests: .P1 --> Data-P1
   * test2: .P1.P2 --> Data-P2
   * test3: .P1.P2.t1 --> Data-P2
   * caseInfo.content.EmbedPage
   */
  static resolveClassID(prop: string, pageReference: string, context: string, baseClassID?: string) {
    const propName = AnnotationProcessor.trimAnnotation(prop);
    let fullRef = propName.startsWith('.') ? `${pageReference}${propName}` : propName;

    while (fullRef) {
      const classID = getValue(Constants.CLASS_ID, fullRef, context);
      if (classID) {
        return classID;
      }
      if (baseClassID) {
        const resolvedClassID = this.resolvePropertyPageClass(fullRef, propName, baseClassID);
        if (resolvedClassID) {
          return resolvedClassID;
        }
      }
      const lastDotIndex = fullRef.lastIndexOf('.');
      if (lastDotIndex === -1) break;
      fullRef = fullRef.substring(0, lastDotIndex);
    }
    return null;
  }
}

export default ContextProcessor;
